import { AccessRequirementEnum, FeatureAccess, useCurrentUserQuery } from '@alice-financial/alice-sdk'
import { Container, Typography } from '@alice-financial/pretext-ui'
import * as React from 'react'
import { FormattedMessage } from 'react-intl'
import { Navigate } from 'react-router'
import { NotFound } from '../routes/NotFound'
import { PageBody } from '../routes/PageBody'
import { useRequiredProfileType } from '../user/sessionProfile/sessionProfileHelpers'
import { AvailableProfileTypes, UserProfileType } from '../user/sessionProfile/types'
import { EmbeddedAuthFormLayout } from './AuthFormLayout'
import { PasswordForm } from './PasswordForm'
import { useLoginPath } from './useLoginPath'

type AuthenticateProps = {
  children: React.ReactNode
  requiredProfile?: UserProfileType | AvailableProfileTypes
  accessCheck?: keyof FeatureAccess
}

/**
 * A component that will redirect to the login page as soon as it
 * determines that the viewer is _not_ logged in.
 *
 * Currently it will still render its children before the user is determined
 * - it is expected that every view can handle its own 'loading'/empty
 * state appropriately
 */
export const Authenticate = ({ children, requiredProfile, accessCheck }: AuthenticateProps) => {
  const { data: currentUserData, isLoading } = useCurrentUserQuery()
  const requiredProfileOptions: AvailableProfileTypes | [null] = requiredProfile
    ? typeof requiredProfile === 'string'
      ? [requiredProfile]
      : requiredProfile
    : [null]
  const requiredProfileType = useRequiredProfileType(...requiredProfileOptions)
  const loginPath = useLoginPath()

  // As soon as we know the user is not authenticated, redirect to login
  if (isLoading || requiredProfileType === undefined) {
    return (
      <PageBody>
        <Container>
          <Typography variant="body2">Loading user information...</Typography>
        </Container>
      </PageBody>
    )
  }
  if (!currentUserData?.currentUser?.id) return <Navigate to={loginPath} replace />
  const accessRequirement = accessCheck
    ? currentUserData?.currentUser?.access[accessCheck]
    : AccessRequirementEnum.Ok

  if (accessRequirement === AccessRequirementEnum.Forbidden) return <NotFound />
  if (requiredProfile && !requiredProfileType) return <NotFound /> // could use '403 Forbidden' in this case

  if (accessRequirement === AccessRequirementEnum.Password) {
    return (
      <EmbeddedAuthFormLayout>
        <Typography gutterBottom>
          <FormattedMessage id="auth.access.password_required" />
        </Typography>
        <PasswordForm />
      </EmbeddedAuthFormLayout>
    )
  }

  return <>{children}</>
}
